<template>
  <div class="pp-tab-surface">
    <div
      v-if="!showFeed"
      class="pp-feed empty-card"
    >
      <div class="head">
        <div>
          <div class="eyebrow">{{ t('property.v2News.projectUpdatesEyebrow') }}</div>
          <h3>{{ t('property.v2News.projectNewsTitle') }}</h3>
        </div>
      </div>
      <div class="empty">
        <p>{{ t('property.v2News.emptyMessage') }}</p>
      </div>
    </div>
    <div
      v-else
      class="pp-feed"
    >
      <div class="head">
        <div>
          <div class="eyebrow">{{ t('property.v2News.developerUpdatesEyebrow') }}</div>
          <h3>
            {{
              developerName
                ? t('property.v2News.newsFrom', { developerName })
                : t('property.v2News.developerNewsFallback')
            }}
          </h3>
        </div>
        <div class="tail">
          <span
            v-if="unreadCount > 0"
            class="dot"
            ><span class="d"></span> {{ t('property.v2News.unreadCount', { count: unreadCount }, unreadCount) }}</span
          >
          <NuxtLink
            class="see"
            :to="'/news'"
            >{{ t('property.v2News.seeAll') }}
            <span
              class="inline-block rtl:-scale-x-100"
              aria-hidden="true"
              >→</span
            ></NuxtLink
          >
        </div>
      </div>

      <div
        v-for="entry in items"
        :key="entry.id"
        class="item"
      >
        <div
          class="ic"
          :class="entry.tag"
        >
          <svg
            v-if="entry.tag === 'launch'"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M12 2l2.5 7.5h7.5l-6 4.5 2.3 7.5L12 17l-6.3 4.5L8 14l-6-4.5h7.5L12 2z" />
          </svg>
          <svg
            v-else-if="entry.tag === 'price'"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            aria-hidden="true"
          >
            <path
              d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <svg
            v-else-if="entry.tag === 'milestone'"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            aria-hidden="true"
          >
            <path
              d="M22 11.08V12a10 10 0 11-5.93-9.14"
              stroke-linecap="round"
            />
            <polyline
              points="22 4 12 14.01 9 11.01"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <svg
            v-else-if="entry.tag === 'incentive'"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M12 2l3 7h7l-5.5 4 2 7L12 16l-6.5 4 2-7L2 9h7z" />
          </svg>
          <svg
            v-else
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            aria-hidden="true"
          >
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
            <path d="M14 2v6h6" />
          </svg>
        </div>
        <div class="body">
          <div class="tag-row">
            <span
              class="tag"
              :class="entry.tag"
              >{{ entry.tagLabel }}</span
            >
            <span class="time">{{ entry.timeLabel }}</span>
            <span
              v-if="!entry.isRead"
              class="unread"
              :aria-label="t('property.v2News.unreadAriaLabel')"
            ></span>
          </div>
          <div class="t">{{ entry.title }}</div>
          <div
            v-if="entry.body"
            class="m"
          >
            {{ entry.body }}
          </div>
          <NuxtLink
            v-if="entry.projectHref"
            :to="entry.projectHref"
            class="cta"
          >
            {{ t('property.v2News.viewProject') }}
            <span
              class="inline-block rtl:-scale-x-100"
              aria-hidden="true"
              >→</span
            >
          </NuxtLink>
        </div>
      </div>

      <div class="foot">
        <NuxtLink :to="'/news'"
          >{{ t('property.v2News.viewAllUpdates') }}
          <span
            class="inline-block rtl:-scale-x-100"
            aria-hidden="true"
            >→</span
          ></NuxtLink
        >
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatDistanceToNowStrict } from 'date-fns';
import type { PropertyDetailed } from '@/types/properties';
import { useNewsListQuery, type NewsItem } from '@/api/queries/useNewsListQuery';

const props = defineProps<{
  item: PropertyDetailed;
  projectName?: string;
}>();

const { t, locale } = useI18n();
const { formatRelativeTime } = useFormat();
const authStore = useAuthStore();

// Bot DB attributes news to DEVELOPERS, not projects — most projects have no
// own activity rows. Filter by the property's developer name (Geniemap
// `developer.title.en`) and let the server resolve it to bot developer ids
// via normalizeDeveloperName.
const developerName = computed<string>(() => {
  const title = props.item?.developer?.title;
  if (typeof title === 'string') return title.trim();
  return (title?.en ?? '').trim();
});

const newsParams = computed(() => ({
  pageSize: 6,
  ...(developerName.value ? { developerName: developerName.value } : {}),
}));

const { data: newsData } = useNewsListQuery(newsParams);

type FeedTag = 'launch' | 'price' | 'doc' | 'milestone' | 'incentive';

const tagFor = (type: NewsItem['type']): FeedTag => {
  switch (type) {
    case 'BROCHURE':
      return 'doc';
    case 'INTEL':
      return 'incentive';
    case 'UPDATE':
      return 'milestone';
    case 'INVENTORY':
      return 'price';
    case 'HANDOVER':
      return 'launch';
    default:
      return 'doc';
  }
};

const TAG_LABEL_KEYS: Record<NewsItem['type'], string> = {
  BROCHURE: 'property.v2News.tags.brochure',
  UPDATE: 'property.v2News.tags.update',
  INVENTORY: 'property.v2News.tags.inventory',
  INTEL: 'property.v2News.tags.intel',
  HANDOVER: 'property.v2News.tags.handover',
};

const tagLabelFor = (type: NewsItem['type']) => (TAG_LABEL_KEYS[type] ? t(TAG_LABEL_KEYS[type]) : type);

type FeedEntry = {
  id: string;
  tag: FeedTag;
  tagLabel: string;
  timeLabel: string;
  title: string;
  body: string;
  isRead: boolean;
  projectHref: string | null;
};

const items = computed<FeedEntry[]>(() => {
  const list = newsData.value?.items ?? [];
  return list.slice(0, 5).map((n) => ({
    id: n.id,
    tag: tagFor(n.type),
    tagLabel: tagLabelFor(n.type),
    timeLabel: (() => {
      try {
        // date-fns Strict has no exact Intl equivalent; keep it for en and use
        // formatRelativeTime (Intl.RelativeTimeFormat) for other locales (P6 escape).
        return locale.value === 'en'
          ? formatDistanceToNowStrict(new Date(n.publishedAt), { addSuffix: true })
          : formatRelativeTime(n.publishedAt);
      } catch {
        return '';
      }
    })(),
    title: n.title,
    body: n.body,
    isRead: !!n.isRead,
    projectHref: n.projectGeniemapId != null ? `/properties/${n.projectGeniemapId}` : null,
  }));
});

const unreadCount = computed(() => items.value.filter((i) => !i.isRead).length);

const showFeed = computed(() => !!authStore.token && items.value.length > 0);
</script>

<style scoped lang="scss">
.pp-tab-surface {
  background: #f8f7f3;
  border-radius: 20px;
  padding: 28px;
}

.pp-feed {
  background: white;
  border: 1px solid rgba(13, 15, 21, 0.08);
  border-radius: 18px;
  padding: 22px 24px 8px;

  &.empty-card .empty {
    padding: 32px 0;
    text-align: center;
    color: rgba(13, 15, 21, 0.62);
    font-size: 13px;
  }

  .head {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 14px;
    flex-wrap: wrap;

    .eyebrow {
      font-family: 'JetBrains Mono', ui-monospace, monospace;
      font-size: 10px;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: rgba(13, 15, 21, 0.62);
      font-weight: 700;
      margin-bottom: 4px;
    }

    h3 {
      font-family: 'Fraunces', 'Times New Roman', serif;
      font-weight: 600;
      font-size: 22px;
      letter-spacing: -0.015em;
      line-height: 1.15;
      margin: 0;
    }

    .tail {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .dot {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      font-family: 'JetBrains Mono', ui-monospace, monospace;
      font-size: 10px;
      font-weight: 700;
      color: #126b2f;
      letter-spacing: 0.06em;
      text-transform: uppercase;

      .d {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #22c55e;
        box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.2);
      }
    }

    .see {
      font-family: 'JetBrains Mono', ui-monospace, monospace;
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: #0d0f15;
    }
  }
}

.item {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  padding: 14px 0;
  border-top: 1px solid rgba(13, 15, 21, 0.08);

  .ic {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: #0d0f15;

    &.launch {
      background: #fff6e7;
      color: #8b6000;
    }

    &.price {
      background: rgba(57, 87, 211, 0.1);
      color: #3957d3;
    }

    &.doc {
      background: #f3f3f3;
      color: rgba(13, 15, 21, 0.7);
    }

    &.milestone {
      background: #e8f9e2;
      color: #126b2f;
    }

    &.incentive {
      background: rgba(255, 122, 89, 0.15);
      color: #b93e1e;
    }
  }

  .body {
    flex: 1;
    min-width: 0;
  }

  .tag-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
    flex-wrap: wrap;

    .tag {
      padding: 2px 7px;
      border-radius: 5px;
      font-family: 'JetBrains Mono', ui-monospace, monospace;
      font-size: 9px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;

      &.launch {
        background: #ffb648;
        color: #0d0f15;
      }

      &.price {
        background: rgba(57, 87, 211, 0.14);
        color: #3957d3;
      }

      &.doc {
        background: #f3f3f3;
        color: rgba(13, 15, 21, 0.7);
      }

      &.milestone {
        background: #e8f9e2;
        color: #126b2f;
      }

      &.incentive {
        background: rgba(255, 122, 89, 0.2);
        color: #b93e1e;
      }
    }

    .time {
      font-family: 'JetBrains Mono', ui-monospace, monospace;
      font-size: 10px;
      color: rgba(13, 15, 21, 0.62);
      font-weight: 600;
      letter-spacing: 0.02em;
    }

    .unread {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: #ff7a59;
    }
  }

  .t {
    font-size: 14px;
    font-weight: 600;
    color: #0d0f15;
    line-height: 1.4;
  }

  .m {
    font-size: 12.5px;
    color: rgba(13, 15, 21, 0.62);
    margin-top: 3px;
    line-height: 1.4;
  }

  .cta {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 10px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #0d0f15;
    font-weight: 700;
    margin-top: 6px;
    display: inline-block;
  }
}

.foot {
  padding: 12px 0;
  border-top: 1px solid rgba(13, 15, 21, 0.08);
  display: flex;
  justify-content: center;

  a {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 10px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #0d0f15;
    font-weight: 700;
  }
}
</style>
